import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Eye, EyeOff, X, Save, BookOpen, ExternalLink, Sparkles, Images, RefreshCw, Wand2, Layers, RotateCw } from "lucide-react";

const CATEGORIES = ["Dependência Química", "Saúde Mental", "Tratamento", "Clínicas de Recuperação", "Profissionais RAF"];

const emptyPost = () => ({
  title: "", excerpt: "", category: CATEGORIES[0],
  hero_image: "", gallery: ["","",""],
  content: "", faq: [{q:"",a:""}],
  read_minutes: 5, author: "Equipe RAF Direct", published: true,
});

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [seedState, setSeedState] = useState(null);
  const [imgState, setImgState] = useState(null);
  const [galState, setGalState] = useState(null);
  const [regenRowId, setRegenRowId] = useState(null);
  const [promptEditor, setPromptEditor] = useState(null);
  const pollRef = useRef(null);
  const imgPollRef = useRef(null);
  const galPollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/blog/posts");
      setPosts(r.data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pollSeed = useCallback(async () => {
    try {
      const r = await api.get("/admin/blog/seed-status");
      setSeedState(r.data);
      if (!r.data.running) {
        clearInterval(pollRef.current); pollRef.current = null;
        if (r.data.error) toast.error("Erro no seed: " + r.data.error);
        else toast.success(`Seed concluído — ${r.data.created} artigos`);
        await load();
      }
    } catch { /* silent */ }
  }, [load]);

  const pollImg = useCallback(async () => {
    try {
      const r = await api.get("/admin/blog/regenerate-ai-status");
      setImgState(r.data);
      if (!r.data.running) {
        clearInterval(imgPollRef.current); imgPollRef.current = null;
        if (r.data.error) toast.error("Erro: " + r.data.error);
        else toast.success(r.data.message || "Imagens regeneradas");
        await load();
      }
    } catch { /* silent */ }
  }, [load]);

  const pollGal = useCallback(async () => {
    try {
      const r = await api.get("/admin/blog/regenerate-ai-gallery-status");
      setGalState(r.data);
      if (!r.data.running) {
        clearInterval(galPollRef.current); galPollRef.current = null;
        if (r.data.error) toast.error("Erro galeria: " + r.data.error);
        else toast.success(r.data.message || "Galeria gerada");
        await load();
      }
    } catch { /* silent */ }
  }, [load]);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (imgPollRef.current) clearInterval(imgPollRef.current);
    if (galPollRef.current) clearInterval(galPollRef.current);
  }, []);

  // Retoma polling ao entrar se alguma geração está em curso
  useEffect(() => {
    (async () => {
      try {
        const [i, g] = await Promise.all([
          api.get("/admin/blog/regenerate-ai-status"),
          api.get("/admin/blog/regenerate-ai-gallery-status"),
        ]);
        if (i.data?.running) {
          setImgState(i.data);
          if (!imgPollRef.current) imgPollRef.current = setInterval(pollImg, 4000);
        }
        if (g.data?.running) {
          setGalState(g.data);
          if (!galPollRef.current) galPollRef.current = setInterval(pollGal, 5000);
        }
      } catch { /* silent */ }
    })();
  }, [pollImg, pollGal]);

  const runSeed = async () => {
    if (!window.confirm("Gerar 30 artigos padrão via GPT-5.2? Leva ~4 minutos.")) return;
    try {
      const r = await api.post("/admin/blog/seed-run");
      if (r.data.already_running) { toast.info("Seed já está rodando"); }
      else { toast.info("Seed iniciado — vou atualizar aqui em tempo real"); }
      if (!pollRef.current) pollRef.current = setInterval(pollSeed, 4000);
      pollSeed();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao iniciar seed"); }
  };

  const regenerateAiImages = async (onlyMissing = false) => {
    const label = onlyMissing ? "apenas dos artigos sem imagem AI" : `de TODOS os ${posts.length} artigos`;
    if (!window.confirm(`Gerar imagens hero via Nano Banana ${label}?\n\nLeva ~5-8min. Custo: ~$0.04/imagem via Emergent LLM Key.`)) return;
    try {
      const r = await api.post(`/admin/blog/regenerate-ai-images?only_missing=${onlyMissing}`);
      if (r.data.already_running) { toast.info("Geração já está rodando"); }
      else { toast.info("Geração iniciada — acompanho aqui"); }
      if (!imgPollRef.current) imgPollRef.current = setInterval(pollImg, 4000);
      pollImg();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao iniciar"); }
  };

  const regenerateGallery = async (onlyMissing = false) => {
    const label = onlyMissing ? "só faltantes" : `TODOS os ${posts.length} artigos`;
    const total = onlyMissing ? "variável" : `${posts.length * 3}`;
    if (!window.confirm(
      `Gerar 3 imagens de apoio (galeria interna) para ${label}?\n\n~${total} imagens no total, ~7s cada. Custo ~$0.04/img via Emergent LLM Key.\nAparecem na página do artigo entre o texto e a FAQ.`
    )) return;
    try {
      const r = await api.post(`/admin/blog/regenerate-ai-gallery?only_missing=${onlyMissing}`);
      if (r.data.already_running) { toast.info("Galeria já está sendo gerada"); }
      else { toast.info("Galeria iniciada — acompanho aqui"); }
      if (!galPollRef.current) galPollRef.current = setInterval(pollGal, 5000);
      pollGal();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao iniciar galeria"); }
  };

  const regenerateOne = async (p) => {
    if (regenRowId) return;
    setRegenRowId(p.id);
    try {
      await api.post(`/admin/blog/posts/${p.id}/regenerate-image`);
      toast.success(`Imagem regerada: ${p.title.slice(0, 40)}...`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao regerar");
    } finally {
      setRegenRowId(null);
    }
  };

  const openPromptEditor = async () => {
    try {
      const r = await api.get("/admin/blog/image-prompts");
      setPromptEditor(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao carregar prompts"); }
  };

  const savePrompts = async () => {
    try {
      await api.put("/admin/blog/image-prompts", {
        categories: promptEditor.categories,
        negative: promptEditor.negative,
      });
      toast.success("Prompts salvos — próximas gerações usarão o novo estilo");
      setPromptEditor(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
  };

  const resetPromptToDefault = (cat) => {
    setPromptEditor(pe => ({
      ...pe,
      categories: { ...pe.categories, [cat]: pe.defaults.categories[cat] },
    }));
  };

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Título é obrigatório"); return; }
    const payload = { ...editing, gallery: (editing.gallery || []).filter(Boolean), faq: (editing.faq || []).filter(f => f.q && f.a) };
    try {
      if (editing.id) {
        await api.put(`/admin/blog/posts/${editing.id}`, payload);
        toast.success("Artigo atualizado");
      } else {
        await api.post("/admin/blog/posts", payload);
        toast.success("Artigo criado");
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Excluir "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/blog/posts/${p.id}`);
      toast.success("Excluído");
      await load();
    } catch { toast.error("Erro ao excluir"); }
  };

  const togglePublished = async (p) => {
    try {
      await api.put(`/admin/blog/posts/${p.id}`, { ...p, published: !p.published });
      await load();
    } catch { toast.error("Erro ao atualizar"); }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="admin-blog-page">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-slate-500 flex items-center gap-1"><BookOpen className="w-3 h-3"/> Admin</div>
            <h1 className="text-2xl font-bold text-[#0B2B62]">Blog · {posts.length} artigos</h1>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            {posts.length > 0 && !imgState?.running && !galState?.running && (
              <>
                <button onClick={openPromptEditor} data-testid="blog-prompt-editor-btn" className="px-3 py-2 rounded-lg bg-slate-100 border border-slate-300 text-slate-700 font-bold text-xs hover:bg-slate-200 transition inline-flex items-center gap-1.5"><Wand2 className="w-3.5 h-3.5"/> Editar prompts</button>
                <button onClick={()=>regenerateAiImages(true)} data-testid="blog-ai-images-missing-btn" className="px-3 py-2 rounded-lg bg-teal-600 text-white font-bold text-xs hover:bg-teal-700 transition inline-flex items-center gap-1.5"><Images className="w-3.5 h-3.5"/> Hero AI (faltantes)</button>
                <button onClick={()=>regenerateAiImages(false)} data-testid="blog-ai-images-all-btn" className="px-3 py-2 rounded-lg bg-emerald-600 text-white font-bold text-xs hover:bg-emerald-700 transition inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5"/> Regenerar heros</button>
                <button onClick={()=>regenerateGallery(true)} data-testid="blog-gallery-missing-btn" className="px-3 py-2 rounded-lg bg-indigo-100 border border-indigo-300 text-indigo-800 font-bold text-xs hover:bg-indigo-200 transition inline-flex items-center gap-1.5"><Layers className="w-3.5 h-3.5"/> Galeria (faltantes)</button>
                <button onClick={()=>regenerateGallery(false)} data-testid="blog-gallery-all-btn" className="px-3 py-2 rounded-lg bg-indigo-600 text-white font-bold text-xs hover:bg-indigo-700 transition inline-flex items-center gap-1.5"><Layers className="w-3.5 h-3.5"/> Gerar galeria (todos)</button>
              </>
            )}
            {posts.length < 30 && !seedState?.running && (
              <button onClick={runSeed} data-testid="blog-seed-btn" className="px-3 py-2 rounded-lg bg-violet-600 text-white font-bold text-xs hover:bg-violet-700 transition inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5"/> Semear 30 artigos</button>
            )}
            <button onClick={()=>setEditing(emptyPost())} data-testid="blog-new-btn" className="px-3 py-2 rounded-lg bg-[#0B2B62] text-white font-bold text-xs hover:bg-[#1E4FB8] transition inline-flex items-center gap-1.5"><Plus className="w-3.5 h-3.5"/> Novo artigo</button>
          </div>
        </div>

        {imgState?.running && (
          <div className="mb-4 rounded-lg bg-emerald-50 border border-emerald-200 p-3" data-testid="blog-ai-images-progress">
            <div className="flex items-center justify-between text-xs font-bold text-emerald-900 mb-1">
              <span><Images className="inline w-3 h-3 mr-1 animate-pulse"/> Gerando hero via Nano Banana</span>
              <span>{imgState.processed} / {imgState.total || "?"}</span>
            </div>
            <div className="w-full h-2 bg-emerald-200 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-600 transition-all" style={{ width: `${(imgState.processed / Math.max(1, imgState.total || 1)) * 100}%` }}/>
            </div>
            <div className="text-[11px] text-emerald-800 mt-1 truncate">{imgState.message}</div>
          </div>
        )}

        {galState?.running && (
          <div className="mb-4 rounded-lg bg-indigo-50 border border-indigo-200 p-3" data-testid="blog-gallery-progress">
            <div className="flex items-center justify-between text-xs font-bold text-indigo-900 mb-1">
              <span><Layers className="inline w-3 h-3 mr-1 animate-pulse"/> Gerando galeria interna (3 imgs/artigo)</span>
              <span>{galState.processed} / {galState.total || "?"}</span>
            </div>
            <div className="w-full h-2 bg-indigo-200 rounded-full overflow-hidden">
              <div className="h-full bg-indigo-600 transition-all" style={{ width: `${(galState.processed / Math.max(1, galState.total || 1)) * 100}%` }}/>
            </div>
            <div className="text-[11px] text-indigo-800 mt-1 truncate">{galState.message}</div>
          </div>
        )}

        {seedState?.running && (
          <div className="mb-4 rounded-lg bg-violet-50 border border-violet-200 p-3" data-testid="blog-seed-progress">
            <div className="flex items-center justify-between text-xs font-bold text-violet-900 mb-1">
              <span><Sparkles className="inline w-3 h-3 mr-1 animate-pulse"/> Gerando artigos via GPT-5.2</span>
              <span>{seedState.created} / {seedState.total}</span>
            </div>
            <div className="w-full h-2 bg-violet-200 rounded-full overflow-hidden">
              <div className="h-full bg-violet-600 transition-all" style={{ width: `${(seedState.created / Math.max(1, seedState.total)) * 100}%` }}/>
            </div>
            <div className="text-[11px] text-violet-800 mt-1 truncate">{seedState.message}</div>
          </div>
        )}

        {loading && <div className="text-center py-16 text-slate-400 text-sm">Carregando...</div>}

        <div className="lg:hidden px-4 pt-2 text-[11px] text-slate-400 italic">← Arraste a tabela para o lado para ver todas as colunas →</div>
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="blog-admin-table">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Título</th>
                <th className="px-4 py-3 text-left">Categoria</th>
                <th className="px-4 py-3 text-center">Publicado</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {posts.map(p => (
                <tr key={p.id} className="hover:bg-slate-50" data-testid={`blog-row-${p.slug}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {p.hero_image ? (
                        <img src={p.hero_image} alt="" className="w-14 h-9 object-cover rounded border border-slate-200 shrink-0" onError={e=>{e.target.style.opacity='0.3';}}/>
                      ) : (
                        <div className="w-14 h-9 rounded bg-slate-100 shrink-0"/>
                      )}
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-800 line-clamp-1">{p.title}</div>
                        <div className="text-xs text-slate-500 line-clamp-1">{p.excerpt}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{p.category}</td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={()=>togglePublished(p)} title={p.published?"Publicado":"Rascunho"} data-testid={`blog-toggle-pub-${p.slug}`}>
                      {p.published ? <Eye className="w-4 h-4 text-emerald-600"/> : <EyeOff className="w-4 h-4 text-slate-400"/>}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={()=>regenerateOne(p)} disabled={regenRowId===p.id} data-testid={`blog-regen-img-${p.slug}`} title="Regerar imagem via IA" className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600 disabled:opacity-40">
                        {regenRowId===p.id ? <RotateCw className="w-4 h-4 animate-spin"/> : <Wand2 className="w-4 h-4"/>}
                      </button>
                      <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`blog-view-${p.slug}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Ver"><ExternalLink className="w-4 h-4"/></a>
                      <button onClick={()=>setEditing({...p, gallery:[...(p.gallery||[]),"","",""].slice(0,3), faq:[...(p.faq||[]),{q:"",a:""}].slice(0, Math.max(1,(p.faq||[]).length))})} data-testid={`blog-edit-${p.slug}`} title="Editar" className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><Edit className="w-4 h-4"/></button>
                      <button onClick={()=>remove(p)} data-testid={`blog-del-${p.slug}`} title="Excluir" className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
                    </div>
                  </td>
                </tr>
              ))}
              {posts.length === 0 && !loading && (
                <tr><td colSpan={4} className="text-center py-12 text-slate-400">Nenhum artigo ainda — clique em "Novo artigo" para criar</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="blog-editor-modal">
          <div className="bg-white rounded-2xl max-w-3xl w-full my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div className="font-bold text-[#0B2B62]">{editing.id ? "Editar artigo" : "Novo artigo"}</div>
              <button onClick={()=>setEditing(null)} className="text-slate-500 hover:text-slate-900" data-testid="blog-editor-close"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-600 mb-1 block">Título *</label>
                <input value={editing.title} onChange={e=>setEditing({...editing, title:e.target.value})} data-testid="blog-editor-title" className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#0B2B62]/30 focus:outline-none"/>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-600 mb-1 block">Categoria</label>
                  <select value={editing.category} onChange={e=>setEditing({...editing, category:e.target.value})} data-testid="blog-editor-category" className="w-full border border-slate-300 rounded-lg px-3 py-2">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 mb-1 block">Minutos de leitura</label>
                  <input type="number" min={1} value={editing.read_minutes} onChange={e=>setEditing({...editing, read_minutes:Number(e.target.value)})} className="w-full border border-slate-300 rounded-lg px-3 py-2"/>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 mb-1 block">Resumo (excerpt)</label>
                <textarea value={editing.excerpt} onChange={e=>setEditing({...editing, excerpt:e.target.value})} rows={2} data-testid="blog-editor-excerpt" className="w-full border border-slate-300 rounded-lg px-3 py-2" maxLength={400}/>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 mb-1 block">Imagem hero (URL)</label>
                <input value={editing.hero_image} onChange={e=>setEditing({...editing, hero_image:e.target.value})} placeholder="/api/public/blog/image/xxxxx ou URL externa" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono"/>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 mb-1 block">Galeria (3 URLs — aparecem na página do artigo)</label>
                {[0,1,2].map(i => (
                  <input key={i} value={(editing.gallery||[])[i]||""} onChange={e=>{ const g=[...(editing.gallery||["","",""])]; g[i]=e.target.value; setEditing({...editing, gallery:g});}} placeholder={`Imagem ${i+1}`} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-mono mb-1"/>
                ))}
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 mb-1 block">Conteúdo (Markdown)</label>
                <textarea value={editing.content} onChange={e=>setEditing({...editing, content:e.target.value})} rows={12} data-testid="blog-editor-content" className="w-full border border-slate-300 rounded-lg px-3 py-2 font-mono text-xs" placeholder="## Subtítulo&#10;&#10;Parágrafo com **negrito**..."/>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold text-slate-600">Perguntas frequentes</label>
                  <button onClick={()=>setEditing({...editing, faq:[...(editing.faq||[]), {q:"",a:""}]})} className="text-xs text-[#0B2B62] hover:underline">+ Adicionar</button>
                </div>
                {(editing.faq||[]).map((f, i) => (
                  <div key={i} className="border border-slate-200 rounded-lg p-2 mb-2 space-y-1">
                    <input value={f.q} onChange={e=>{ const nf=[...editing.faq]; nf[i]={...nf[i], q:e.target.value}; setEditing({...editing, faq:nf});}} placeholder="Pergunta" className="w-full border-b border-slate-200 py-1 text-sm focus:outline-none"/>
                    <textarea value={f.a} onChange={e=>{ const nf=[...editing.faq]; nf[i]={...nf[i], a:e.target.value}; setEditing({...editing, faq:nf});}} rows={2} placeholder="Resposta" className="w-full py-1 text-sm focus:outline-none"/>
                  </div>
                ))}
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.published} onChange={e=>setEditing({...editing, published:e.target.checked})} data-testid="blog-editor-published"/>
                Publicado
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
              <button onClick={()=>setEditing(null)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-200 text-sm">Cancelar</button>
              <button onClick={save} data-testid="blog-editor-save" className="px-4 py-2 rounded-lg bg-[#0B2B62] text-white font-bold text-sm hover:bg-[#1E4FB8] transition inline-flex items-center gap-2"><Save className="w-4 h-4"/> Salvar</button>
            </div>
          </div>
        </div>
      )}

      {promptEditor && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="blog-prompt-editor-modal">
          <div className="bg-white rounded-2xl max-w-3xl w-full my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <div className="font-bold text-[#0B2B62] flex items-center gap-2"><Wand2 className="w-4 h-4"/> Editor de prompts (Nano Banana)</div>
                <div className="text-xs text-slate-500 mt-0.5">Ajuste o estilo visual de cada categoria. Vale para as próximas gerações.</div>
              </div>
              <button onClick={()=>setPromptEditor(null)} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
              {CATEGORIES.map(cat => (
                <div key={cat} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-bold text-[#0B2B62]">{cat}</label>
                    <button onClick={()=>resetPromptToDefault(cat)} className="text-[10px] text-slate-500 hover:text-slate-800 underline">Resetar para default</button>
                  </div>
                  <textarea
                    value={promptEditor.categories[cat] || ""}
                    onChange={e=>setPromptEditor(pe=>({...pe, categories:{...pe.categories, [cat]: e.target.value}}))}
                    rows={4}
                    data-testid={`prompt-input-${cat}`}
                    className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs font-mono"
                  />
                </div>
              ))}
              <div className="border border-red-200 rounded-lg p-3 bg-red-50/40">
                <label className="text-xs font-bold text-red-800 mb-1.5 block">Prompt NEGATIVO (o que NUNCA aparecer)</label>
                <textarea
                  value={promptEditor.negative || ""}
                  onChange={e=>setPromptEditor(pe=>({...pe, negative: e.target.value}))}
                  rows={4}
                  data-testid="prompt-input-negative"
                  className="w-full border border-red-300 rounded px-2 py-1.5 text-xs font-mono bg-white"
                />
                <button onClick={()=>setPromptEditor(pe=>({...pe, negative: pe.defaults.negative}))} className="mt-1 text-[10px] text-red-700 underline">Resetar negative</button>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
              <button onClick={()=>setPromptEditor(null)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-200 text-sm">Cancelar</button>
              <button onClick={savePrompts} data-testid="blog-prompt-save" className="px-4 py-2 rounded-lg bg-[#0B2B62] text-white font-bold text-sm hover:bg-[#1E4FB8] transition inline-flex items-center gap-2"><Save className="w-4 h-4"/> Salvar prompts</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
